import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewChild,
    signal,
    computed,
    Input,
    forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
    selector: "app-code-highlight",
    template: `
        <div class="editor-container">
      <textarea #codeArea
                [value]="codeContent()"
                (input)="onInput($event)"
                (scroll)="syncScroll(codeArea.scrollTop)"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                class="code-input"
      ></textarea>

            <pre #highlightBlock
                 class="highlight-output"
                 aria-hidden="true"
                 [innerHTML]="highlightedCode()"
            ></pre>

            <div class="line-numbers" [innerHTML]="lineNumbers()"></div>
        </div>
    `,
    styles: [`
        .editor-container {
            position: relative;
            width: 100%;
            height: 400px;
            line-height: 1.5;
            font-size: 14px;
            white-space: pre-wrap;
            tab-size: 4;
            border-radius: 4px;
            overflow: hidden;
            border: 1px solid var(--border-color, rgba(0, 0, 0, 0.12));
            background: #2d2d2d;
            color: white;
            font-family: 'Courier New', 'Consolas', monospace;
        }

        .code-input, .highlight-output {
            padding: 10px;
            margin: 0;
            border: none;
            box-sizing: border-box;
            position: absolute;
            top: 0;
            left: 60px;
            height: 100%;
            width: calc(100% - 60px);
            overflow: auto;
            font-family: inherit;
            line-height: inherit;
            font-size: inherit;
            tab-size: inherit;
            white-space: pre;
        }

        .code-input {
            color: transparent;
            background: transparent;
            caret-color: white;
            z-index: 10;
            resize: none;
            outline: none;
        }

        .code-input:focus {
            outline: none;
        }

        .highlight-output {
            color: #ccc;
            z-index: 5;
            overflow-y: hidden;
            overflow-x: auto;
            pointer-events: none;
        }

        .line-numbers {
            position: absolute;
            top: 0;
            left: 0;
            width: 60px;
            height: 100%;
            line-height: inherit;
            font-size: inherit;
            padding: 10px 0;
            text-align: right;
            padding-right: 10px;
            border-right: 1px solid #444;
            background-color: #333;
            color: #858585;
            overflow: hidden;
            user-select: none;
            z-index: 15;
        }

        ::ng-deep .keyword {
            color: #569cd6;
            font-weight: bold;
        }

        ::ng-deep .string {
            color: #ce9178;
        }

        ::ng-deep .comment {
            color: #6a9955;
        }

        ::ng-deep .variable {
            color: #9cdcfe;
        }

        ::ng-deep .punctuation {
            color: #d4d4d4;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CodeHighlightComponent),
            multi: true,
        },
    ],
})
export class CodeHighlightComponent implements ControlValueAccessor {
    @ViewChild("codeArea") codeArea!: ElementRef<HTMLTextAreaElement>;
    @ViewChild("highlightBlock") highlightBlock!: ElementRef<HTMLPreElement>;

    @Input() language: "json" | "typescript" | "javascript" = "json";

    codeContent = signal<string>("");

    // ControlValueAccessor callbacks
    private onChange: (value: string) => void = () => {
    };
    private onTouched: () => void = () => {
    };

    highlightedCode = computed(() => {
        let content = this.codeContent();

        // Sanitize for HTML display
        content = content
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");

        if (this.language === "json") {
            // JSON syntax highlighting
            // Strings first (to avoid highlighting keywords inside strings)
            content = content.replace(/("(?:\\.|[^"\\])*")/g, "<span class=\"string\">$1</span>");
            content = content.replace(/('(?:\\.|[^'\\])*')/g, "<span class=\"string\">$1</span>");

            // Numbers
            content = content.replace(/\b(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\b/g, "<span class=\"keyword\">$1</span>");

            // Keywords (true, false, null)
            content = content.replace(/\b(true|false|null)\b/g, "<span class=\"keyword\">$1</span>");

            // JSON keys (property names followed by colon)
            content = content.replace(/(<span class="string">"[^"]*"<\/span>)(\s*:)/g, "<span class=\"variable\">$1</span>$2");
        } else {
            // TypeScript/JavaScript syntax highlighting
            const keywords = ["import", "from", "export", "class", "extends", "implements", "interface", "const", "let", "var", "if", "else", "for", "while", "new", "return", "this", "public", "private", "get", "set", "static", "function", "constructor", "debugger", "try", "catch", "finally", "do", "continue", "break", "typeof", "instanceof", "void", "with", "as", "await", "async"];

            // Strings first
            content = content.replace(/("(?:\\.|[^"\\])*")/g, "<span class=\"string\">$1</span>");
            content = content.replace(/('(?:\\.|[^'\\])*')/g, "<span class=\"string\">$1</span>");
            content = content.replace(/(`(?:\\.|[^`\\])*`)/g, "<span class=\"string\">$1</span>");

            // Keywords
            keywords.forEach(keyword => {
                const regex = new RegExp(`\\b(${keyword})\\b`, "g");
                content = content.replace(regex, "<span class=\"keyword\">$1</span>");
            });

            // Decorators
            content = content.replace(/(@[A-Za-z]+)/g, "<span class=\"variable\">$1</span>");

            // Comments
            content = content.replace(/(\/\/[^\n]*)/g, "<span class=\"comment\">$1</span>");
        }

        content += "\n";
        return content;
    });

    lineNumbers = computed(() => {
        const code = this.codeContent();
        const numberOfLines = (code.match(/\n/g) || []).length + 1;
        return Array.from({ length: numberOfLines }, (_, i) => i + 1).join("\n");
    });

    onInput(event: Event): void {
        const textarea = event.target as HTMLTextAreaElement;
        const value = textarea.value;
        this.codeContent.set(value);
        this.onChange(value);
        this.onTouched();
    }

    syncScroll(scrollTop: number): void {
        if (this.highlightBlock) {
            this.highlightBlock.nativeElement.scrollTop = scrollTop;
        }
    }

    // ControlValueAccessor implementation
    writeValue(value: string): void {
        this.codeContent.set(value || "");
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // Handle disabled state if needed
    }
}
